import { ExternalLink, FileCheck2, Loader } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { legalUrl } from '~/helpers/legal/legal-urls'
import { useLegalAcceptance } from '~/helpers/legal/use-legal-acceptance'

/**
 * Acceptation des CGU (et des CGV pour l'admin d'un organisme) après connexion (#360).
 *
 * Contrairement à la notice de suivi, la fenêtre ne se ferme pas : les CGU sont un
 * contrat, et une fenêtre qu'on écarte ne prouve rien. On accepte, ou on se
 * déconnecte. Elle revient à chaque nouvelle version d'un document.
 */
function LegalAcceptanceDialog() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { pending, accept, accepting, refetch } = useLegalAcceptance()

  if (pending.length === 0) return null

  const onAccept = async () => {
    try {
      await accept(pending.map(item => item.document))
    } catch {
      toast.error(t('legal.acceptance.error'))
      // L'état a pu changer ailleurs (autre onglet, nouvelle version) : on le relit
      // plutôt que de laisser la fenêtre sur une liste périmée.
      await refetch()
    }
  }

  return (
    <Dialog open>
      <DialogContent
        className="max-w-xl"
        showCloseButton={false}
        onEscapeKeyDown={event => event.preventDefault()}
        onPointerDownOutside={event => event.preventDefault()}
        onInteractOutside={event => event.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary-text">
            <FileCheck2 className="size-5" aria-hidden />
            <DialogTitle>{t('legal.acceptance.title')}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">{t('legal.acceptance.intro')}</DialogDescription>
        </DialogHeader>

        <ul className="space-y-2">
          {pending.map(item => (
            <li key={item.document}>
              <a
                href={legalUrl(item.document, i18n.language)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm hover:bg-muted"
              >
                <span className="font-medium">{t(`legal.document.${item.document}`)}</span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  {t('legal.acceptance.version', { version: new Date(item.version).toLocaleDateString(i18n.language, { dateStyle: 'long', timeZone: 'UTC' }) })}
                  <ExternalLink className="size-4" aria-hidden />
                  <span className="sr-only">{t('legal.links.newTab')}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => navigate('/logout')}>{t('common.logout')}</Button>
          <Button onClick={onAccept} disabled={accepting}>
            {accepting && <Loader className="mr-2 size-4 animate-spin" aria-hidden />}
            {t('legal.acceptance.accept')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LegalAcceptanceDialog
